/**
 * Admin Store - Zustand store for admin authentication
 * 
 * Manages admin authentication state separately from regular user auth
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import axios from 'axios';
import { AdminUser, AdminDashboardStats, AdminAuthState } from '../types';

// API configuration - Production ready
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';

interface AdminStoreActions {
  // Actions
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  verifyToken: () => Promise<boolean>;
  clearError: () => void;
  getDashboardStats: () => Promise<void>;

  // Internal actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setAdmin: (admin: AdminUser | null, token: string | null) => void;
}

type AdminStore = AdminAuthState & AdminStoreActions;

// Create axios instance for admin API calls
const adminApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
adminApi.interceptors.request.use(
  (config) => {
    const { token } = useAdminStore.getState();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Auto-logout on 401
      useAdminStore.getState().logout();
    }
    return Promise.reject(error);
  }
);

export const useAdminStore = create<AdminStore>()(
  persist(
    (set, get) => ({
      // Initial state
      admin: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      dashboardStats: null,

      // Actions
      login: async (username: string, password: string): Promise<boolean> => {
        // Security: Log admin access attempts (production should use proper logging service)
        if (import.meta.env.PROD) {
          console.warn('Admin login attempt detected');
        }
        
        set({ isLoading: true, error: null });

        try {
          const response = await adminApi.post('/admin/login', {
            username,
            password,
          });

          // Backend returns: { success: true, data: { token, admin }, timestamp, requestId }
          const responseData = response.data.data || response.data;
          const { token, admin } = responseData;

          if (token && admin) {
            set({
              admin,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
            
            // Security: Log successful admin access
            if (import.meta.env.PROD) {
              console.warn('Admin authenticated successfully');
            }
            return true;
          } else {
            set({
              error: 'Invalid response from server',
              isLoading: false,
            });
            return false;
          }
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 
            error.response?.data?.error || 
            error.message || 
            'Login failed';

          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false,
            admin: null,
            token: null,
          });

          console.error('Admin login error:', error);
          return false;
        }
      },

      logout: () => {
        set({
          admin: null,
          token: null,
          isAuthenticated: false,
          error: null,
          dashboardStats: null,
        });
      },

      verifyToken: async (): Promise<boolean> => {
        const { token } = get();
        
        if (!token) {
          set({ isAuthenticated: false });
          return false;
        }

        try {
          const response = await adminApi.get('/admin/verify');
          const { admin } = response.data;

          if (admin) {
            set({
              admin,
              isAuthenticated: true,
              error: null,
            });
            return true;
          } else {
            set({
              isAuthenticated: false,
              admin: null,
              token: null,
            });
            return false;
          }
        } catch (error: any) {
          console.error('Token verification failed:', error);
          set({
            isAuthenticated: false,
            admin: null,
            token: null,
          });
          return false;
        }
      },

      getDashboardStats: async (): Promise<void> => {
        const { isAuthenticated } = get();
        
        if (!isAuthenticated) {
          return;
        }

        try {
          const response = await adminApi.get('/admin/dashboard/stats');
          
          // Backend wraps in { data: { stats: ... } }
          const stats = response.data.data?.stats || response.data.stats;

          set({
            dashboardStats: stats,
          });
        } catch (error: any) {
          console.error('Failed to get dashboard stats:', error);
          set({
            error: 'Failed to load dashboard statistics',
          });
        }
      },

      clearError: () => set({ error: null }),

      setLoading: (loading: boolean) => set({ isLoading: loading }),

      setError: (error: string | null) => set({ error }),

      setAdmin: (admin: AdminUser | null, token: string | null) => {
        set({
          admin,
          token,
          isAuthenticated: !!admin && !!token,
        });
      },
    }),
    {
      name: 'admin-auth', // localStorage key
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        admin: state.admin,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Hook for easy access to admin auth status
export const useAdminAuth = () => {
  const { admin, isAuthenticated, isLoading, error } = useAdminStore();
  
  return {
    admin,
    isAuthenticated,
    isLoading,
    error,
    isAdmin: isAuthenticated && admin?.role === 'admin',
  };
};

// Export adminApi for direct use in components
export { adminApi };
